import React, { useState, useEffect } from 'react';
import { getRetailerVisualizerLink } from '../../../services/api';
import colors from '../../../theme/colors';

export default function IntegrateRoomViz() {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeOption, setActiveOption] = useState(null);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const token = localStorage.getItem('retailer_token');
        
        const res = await getRetailerVisualizerLink();
        const linkUrl = res?.data?.visualizer_link || '';
        setLink(linkUrl);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, []);

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const iframeCode = `<iframe src="${link}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
  const buttonCode = `<a href="${link}" referrerpolicy="strict-origin-when-cross-origin" target="_blank">Visualizer</a>`;

  const integrationOptions = [
    {
      id: 'iframe',
      title: 'Iframe Embed',
      description: 'Embed the visualizer directly on your website as an iframe',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      code: iframeCode
    },
    {
      id: 'button',
      title: 'Button Link',
      description: 'Add a button or anchor tag that opens the visualizer in a new tab',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      ),
      code: buttonCode
    },
    {
      id: 'link',
      title: 'Direct Link',
      description: 'Share the direct URL to your visualizer',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      code: link
    }
  ];

  return (
    <div 
      className="rounded-xl border p-6 shadow-sm relative"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
    >
      <div className="mb-6">
        <h3 
          className="text-lg font-semibold mb-2"
          style={{ color: colors.t1 }}
        >
          Integrate Nitya AI On Your Website
        </h3>
        <p 
          className="text-sm"
          style={{ color: colors.t2 }}
        >
          Choose how you want to integrate the visualizer on your website.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div 
              key={i}
              className="h-32 rounded-lg animate-pulse"
              style={{ backgroundColor: colors.border + '40' }}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {integrationOptions.map((option) => (
            <div
              key={option.id}
              className="p-4 rounded-xl border transition-all duration-200 hover:shadow-sm flex flex-col justify-between"
              style={{ 
                backgroundColor: colors.accent + '08',
                borderColor: colors.border
              }}
            >
              <div>
                <div 
                  className="flex items-center justify-center w-10 h-10 rounded-lg mb-3"
                  style={{ backgroundColor: colors.accent + '18' }}
                >
                  <span style={{ color: colors.accent }}>{option.icon}</span>
                </div>
                
                <h4 
                  className="font-semibold text-sm mb-2"
                  style={{ color: colors.t1 }}
                >
                  {option.title}
                </h4>
                
                <p 
                  className="text-xs mb-4"
                  style={{ color: colors.t2 }}
                >
                  {option.description}
                </p>
              </div>
              
              <button
                onClick={() => setActiveOption(option)}
                className="w-full text-xs font-medium py-2 px-3 rounded-lg transition-colors cursor-pointer"
                style={{ 
                  backgroundColor: colors.accent + '20',
                  color: colors.accent
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent + '30'}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.accent + '20'}
              >
                Get Integration Code
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Code Integration Modal */}
      {activeOption && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div 
            className="w-full max-w-2xl rounded-xl border p-6 shadow-xl transition-all"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-2">
              <h3 
                className="text-lg font-bold"
                style={{ color: colors.t1 }}
              >
                Integration Code
              </h3>
              <button 
                onClick={() => { setActiveOption(null); setCopied(false); }}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer text-xl font-semibold"
              >
                &times;
              </button>
            </div>

            <p 
              className="text-xs mb-4"
              style={{ color: colors.t2 }}
            >
              Place this in any button, anchor tag, or use it with a custom redirect action.
            </p>

            {/* Wrapped Code Box with Copy Button aligned at the end */}
            <div className="bg-[#212121] rounded-lg p-4 font-mono text-sm text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <code className="text-gray-200 whitespace-pre-wrap break-all leading-relaxed flex-1">
                {activeOption.code}
              </code>

              <button
                onClick={() => handleCopy(activeOption.code)}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white text-gray-900 text-xs font-semibold rounded-md hover:bg-gray-100 transition-colors shrink-0 cursor-pointer self-end sm:self-center"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Modal Footer Action */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => { setActiveOption(null); setCopied(false); }}
                className="px-4 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer"
                style={{ borderColor: colors.border, color: colors.t1 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
